import { memo, useRef, useState } from 'react';

import { Plus, Trash2Icon, UploadIcon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { Spinner, Text } from '@/_components';
import { ActionButton } from '@/_containers/actions';
import type { FileQuerySchema } from '@/_schemas/file';
import { formatFileSize } from '@/_shared';

import { FileDownloadButton } from './file-download-button';
import { FilePreview } from './file-preview';

interface SingleFileViewProps {
  file?: FileQuerySchema;
  onRemove: (fileId: number) => void;
  onAdd: () => void;
  disabled: boolean;
  loading?: boolean;
  emptyPlaceholder?: string;
  onUpdate?: (fileId: number, file: File) => Promise<void>;
  allowDelete?: boolean;

  className?: string;
  labelClassName?: string;
  sizeClassName?: string;
  downloadButtonClassName?: string;
}

function SingleFileViewComponent({
  file,
  onAdd,
  disabled,
  loading = false,
  emptyPlaceholder = 'Click to add a file',
  onUpdate,
  onRemove,
  allowDelete,
  className,
  labelClassName,
  sizeClassName,
  downloadButtonClassName,
}: SingleFileViewProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // const handleOpenFile = () => {
  //   if (file?.url) {
  //     window.open(file.url, '_blank', 'noopener,noreferrer');
  //   }
  // };

  const handleEditClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (file && event.target.files && event.target.files.length > 0 && onUpdate) {
      try {
        setIsUpdating(true);
        const selectedFile = event.target.files[0];
        // Call the onUpdate handler to update the file
        await onUpdate(file.id, selectedFile);
        // Reset input value to allow selecting the same file again
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        console.error('File update failed:', error);
      } finally {
        setIsUpdating(false);
      }
    }
  };

  if (!file) {
    return (
      <div className="mt-2 flex items-center justify-center rounded-md border border-dashed border-gray-300 bg-gray-50 py-2 dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-col items-center space-y-2 px-4 py-2 text-center">
          {loading ? (
            <Spinner size="sm" />
          ) : disabled ? (
            <Text className="text-c-card-muted text-xs">No file selected</Text>
          ) : (
            <ActionButton
              size="sm"
              title="Add File"
              tag="button"
              icon={Plus}
              type="primary"
              onClick={onAdd}
              disabled={disabled}
            />
          )}
          {!disabled && <Text className="text-c-card-muted text-xs">{loading ? 'Updating...' : emptyPlaceholder}</Text>}
        </div>
      </div>
    );
  }

  return (
    <div className={twMerge('flex items-start overflow-hidden rounded-md', className)}>
      <div className="flex w-full items-start space-x-4 py-3">
        <FilePreview filename={file?.name ?? ''} fileUrl={file.url} mimeType={file?.mime_type ?? ''} />
        <div className="flex flex-1 flex-col">
          <div className="flex items-center gap-1">
            <Text
              className={twMerge(
                'text-c-card-foreground line-clamp-1 overflow-hidden break-all text-ellipsis',
                labelClassName
              )}
            >
              {file.name}
            </Text>
          </div>
          <Text className={twMerge('text-c-card-muted text-xs', sizeClassName)}>
            {formatFileSize(file.file_size || 0)}
          </Text>
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <FileDownloadButton fileUrl={file.url} fileName={file?.name ?? ''} />
          {!disabled && onUpdate && (
            <>
              <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileChange} accept="*/*" />
              <ActionButton
                size="sm"
                title="Replace File"
                tag="button"
                icon={UploadIcon}
                type="icon"
                onClick={handleEditClick}
                showLoader={isUpdating}
                className={downloadButtonClassName}
              />
              {allowDelete && (
                <ActionButton
                  size="sm"
                  title="Delete File"
                  tag="button"
                  icon={Trash2Icon}
                  type="icon"
                  onClick={() => onRemove(file.id)}
                  showLoader={loading}
                  className={downloadButtonClassName}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export const SingleFileView = memo(SingleFileViewComponent);
